import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  FiUsers, FiUserCheck, FiUserX, FiCreditCard, FiCheck, FiX,
  FiAward, FiStar, FiZap, FiShield, FiActivity,
  FiLock, FiGlobe, FiLifeBuoy, FiHelpCircle, FiMail, FiTrendingUp, FiSettings, FiInfo,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { subscriptionAPI } from '../../services/api';
import PageHeader from '../../components/PageHeader/PageHeader';
import './Subscription.scss';

const PLAN_ICONS = { basic: FiStar, enterprise: FiAward };

// Frontend feature matrix — the backend only stores plan key/label/description,
// so the comparison content lives here (translatable, no fabricated pricing).
const COMPARE_FEATURES = [
  { key: 'contracts',   icon: FiCreditCard, basic: true,  enterprise: true },
  { key: 'templates',   icon: FiZap,        basic: true,  enterprise: true },
  { key: 'documents',   icon: FiActivity,   basic: true,  enterprise: true },
  { key: 'approvals',   icon: FiCheck,      basic: true,  enterprise: true },
  { key: 'audit',       icon: FiShield,     basic: true,  enterprise: true },
  { key: 'branding',    icon: FiStar,       basic: true,  enterprise: true },
  { key: 'permissions', icon: FiLock,       basic: false, enterprise: true },
  { key: 'sso',         icon: FiGlobe,      basic: false, enterprise: true },
  { key: 'api',         icon: FiZap,        basic: false, enterprise: true },
  { key: 'support',     icon: FiLifeBuoy,   basic: false, enterprise: true },
  { key: 'sla',         icon: FiAward,      basic: false, enterprise: true },
];

const FEATURE_LABELS = {
  contracts:   'Contract management',
  templates:   'Contract templates',
  documents:   'Document library',
  approvals:   'Approval workflows',
  audit:       'Audit log',
  branding:    'Custom branding',
  permissions: 'Advanced permissions',
  sso:         'SSO / SAML sign-in',
  api:         'API access',
  support:     'Priority support',
  sla:         'Dedicated SLA & manager',
};

function hexToRgb(hex) {
  if (!hex) return '99, 102, 241';
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(v, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

// Animated donut showing the active-member ratio.
function Ring({ value, max, size = 88, stroke = 9 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  const [draw, setDraw] = useState(0);
  useEffect(() => { const t = setTimeout(() => setDraw(pct), 80); return () => clearTimeout(t); }, [pct]);
  return (
    <svg width={size} height={size} className="sub-ring" viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeOpacity="0.12" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke="var(--sub-accent)" strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={circ - draw * circ}
        style={{ transition: 'stroke-dashoffset 900ms cubic-bezier(0.4,0,0.2,1)', transform: 'rotate(-90deg)', transformOrigin: 'center' }}
      />
      <text x="50%" y="50%" textAnchor="middle" dy="0.34em" className="sub-ring__label">
        {Math.round(pct * 100)}%
      </text>
    </svg>
  );
}

export default function Subscription() {
  const { t } = useTranslation();
  const accent = useSelector(
    (s) => s.settings?.branding?.accentColor
      || s.settings?.config?.branding?.accentColor
      || '#6366f1'
  );
  const accentRgb = useMemo(() => hexToRgb(accent), [accent]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await subscriptionAPI.get();
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || t('subscription.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // SEC-30: tenant self-service plan switching (PUT /subscription) was removed
  // from the backend — plan changes are a platform-administrator operation.
  // The plan cards below are informational only.

  const featureLabel = (key) => t(`subscription.features.${key}`, { defaultValue: FEATURE_LABELS[key] || key });
  const planHighlights = (planKey) =>
    COMPARE_FEATURES.filter(f => f[planKey]).map(f => featureLabel(f.key));

  if (loading) {
    return (
      <div className="sub">
        <div className="sub-skel-head" />
        <div className="sub-skel-grid">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="sub-skel-card" />)}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { usage, plan, planMeta, availablePlans } = data;
  const CurrentIcon = PLAN_ICONS[plan] || FiStar;
  const activeRate = usage.totalUsers > 0 ? Math.round((usage.activeUsers / usage.totalUsers) * 100) : 0;

  const usageCards = [
    { key: 'total',    label: t('subscription.totalUsers'),    value: usage.totalUsers,    icon: FiUsers,     tone: 'accent' },
    { key: 'active',   label: t('subscription.activeUsers'),   value: usage.activeUsers,   icon: FiUserCheck, tone: 'green' },
    { key: 'inactive', label: t('subscription.inactiveUsers'), value: usage.inactiveUsers, icon: FiUserX,     tone: 'gray' },
  ];

  return (
    <div className="sub" style={{ '--sub-accent': accent, '--sub-accent-rgb': accentRgb }}>
      {/* ── Header ── */}
      <PageHeader
        label={t('nav.administration', 'Administration')}
        labelIcon={<FiSettings />}
        title={t('subscription.title')}
        description={t('subscription.subtitle')}
      />

      {/* ── Current plan banner ── */}
      <section className="sub-current">
        <div className="sub-current__main">
          <div className="sub-current__icon"><CurrentIcon /></div>
          <div className="sub-current__text">
            <span className="sub-current__eyebrow">
              {t('subscription.currentPlan')}
              <span className="sub-status"><span className="sub-status__dot" /> {t('subscription.activePlan')}</span>
            </span>
            <h2 className="sub-current__name">{planMeta?.label || plan}</h2>
            <p className="sub-current__desc">{planMeta?.description}</p>
          </div>
        </div>
        <div className="sub-current__aside">
          <Ring value={usage.activeUsers} max={usage.totalUsers} />
          <div className="sub-current__aside-text">
            <strong>{usage.activeUsers}/{usage.totalUsers}</strong>
            <span>{t('subscription.membersActive', { defaultValue: 'members active' })}</span>
          </div>
        </div>
      </section>

      {/* ── Usage ── */}
      <section className="sub-section">
        <div className="sub-section__head">
          <div>
            <h2 className="sub-section__title">{t('subscription.userUsage')}</h2>
            <p className="sub-section__sub">{t('subscription.userUsageDesc')}</p>
          </div>
          <span className="sub-section__pill">
            <FiTrendingUp /> {t('subscription.activeRate', { defaultValue: 'Active rate' })} {activeRate}%
          </span>
        </div>
        <div className="sub-usage">
          {usageCards.map((c) => (
            <div key={c.key} className="sub-usage-card">
              <div className={`sub-usage-card__icon sub-usage-card__icon--${c.tone}`}><c.icon /></div>
              <div className="sub-usage-card__body">
                <span className="sub-usage-card__label">{c.label}</span>
                <span className="sub-usage-card__value">{c.value}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="sub-usage-bar">
          <div className="sub-usage-bar__head">
            <span>{t('subscription.activeUsers')}</span>
            <span className="sub-usage-bar__nums">{usage.activeUsers} / {usage.totalUsers}</span>
          </div>
          <div className="sub-usage-bar__track">
            <div className="sub-usage-bar__fill" style={{ width: `${activeRate}%` }} />
          </div>
        </div>
      </section>

      {/* ── Plans ── */}
      <section className="sub-section">
        <div className="sub-section__head">
          <div>
            <h2 className="sub-section__title">{t('subscription.availablePlans', { defaultValue: 'Available plans' })}</h2>
            <p className="sub-section__sub">{t('subscription.planChangeManaged')}</p>
          </div>
        </div>
        {/* SEC-30: plan changes are handled by the platform administrator */}
        <div className="sub-note" role="note">
          <FiInfo />
          <div>
            <strong>{t('subscription.planChangeManaged')}</strong>
            <span>{t('subscription.planChangeManagedDesc')}</span>
          </div>
        </div>
        <div className="sub-plans">
          {availablePlans.map((p) => {
            const Icon = PLAN_ICONS[p.key] || FiStar;
            const isCurrent = plan === p.key;
            const recommended = p.key === 'enterprise';
            const highlights = planHighlights(p.key).slice(0, 6);
            return (
              <article key={p.key} className={`sub-plan${isCurrent ? ' sub-plan--current' : ''}${recommended ? ' sub-plan--featured' : ''}`}>
                {recommended && !isCurrent && (
                  <span className="sub-plan__ribbon">{t('subscription.recommended', { defaultValue: 'Recommended' })}</span>
                )}
                {isCurrent && (
                  <span className="sub-plan__ribbon sub-plan__ribbon--current">
                    <FiCheck /> {t('subscription.currentPlanBadge')}
                  </span>
                )}
                <div className="sub-plan__top">
                  <div className={`sub-plan__icon sub-plan__icon--${p.key}`}><Icon /></div>
                  <h3 className="sub-plan__name">{p.label}</h3>
                  <p className="sub-plan__desc">{p.description}</p>
                </div>
                <ul className="sub-plan__features">
                  {highlights.map((h, i) => (
                    <li key={i}><FiCheck /> {h}</li>
                  ))}
                </ul>
                {isCurrent && (
                  <span className="sub-plan__btn sub-plan__btn--current" aria-disabled="true">
                    {t('subscription.activePlan')}
                  </span>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {/* ── Feature comparison ── */}
      <section className="sub-section">
        <div className="sub-section__head">
          <div>
            <h2 className="sub-section__title">{t('subscription.compareTitle', { defaultValue: 'Compare features' })}</h2>
            <p className="sub-section__sub">{t('subscription.compareSub', { defaultValue: 'Everything included in each plan, side by side.' })}</p>
          </div>
        </div>
        <div className="sub-compare">
          <div className="sub-compare__row sub-compare__row--head">
            <span className="sub-compare__feature">{t('subscription.featureCol', { defaultValue: 'Feature' })}</span>
            {availablePlans.map((p) => (
              <span key={p.key} className={`sub-compare__col${plan === p.key ? ' sub-compare__col--current' : ''}`}>
                {p.label}
                {plan === p.key && <em>{t('subscription.activePlan')}</em>}
              </span>
            ))}
          </div>
          {COMPARE_FEATURES.map((f) => (
            <div key={f.key} className="sub-compare__row">
              <span className="sub-compare__feature">
                <f.icon /> {featureLabel(f.key)}
              </span>
              {availablePlans.map((p) => (
                <span key={p.key} className={`sub-compare__col${plan === p.key ? ' sub-compare__col--current' : ''}`}>
                  {f[p.key]
                    ? <span className="sub-compare__yes"><FiCheck /></span>
                    : <span className="sub-compare__no"><FiX /></span>}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ── Help / contact ── */}
      <section className="sub-help">
        <div className="sub-help__icon"><FiHelpCircle /></div>
        <div className="sub-help__text">
          <h3>{t('subscription.helpTitle', { defaultValue: 'Questions about your plan?' })}</h3>
          <p>{t('subscription.helpSub', { defaultValue: 'Our team can help you pick the right plan or set up a custom enterprise agreement.' })}</p>
        </div>
        <a className="sub-help__btn" href="mailto:support@example.com">
          <FiMail /> {t('subscription.contactSupport', { defaultValue: 'Contact support' })}
        </a>
      </section>
    </div>
  );
}
